/**
 * Reusable accordion section with collapse/expand animation.
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

interface AccordionSectionProps {
  title: string
  icon: React.ReactNode
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}

export const AccordionSection: React.FC<AccordionSectionProps> = ({
  title, icon, isOpen, onToggle, children,
}) => (
  <div className="border border-border rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 tap-highlight-transparent hover:bg-white/[0.03] transition-colors"
    >
      <span className="text-primary">{icon}</span>
      <span className="flex-1 text-left text-sm font-display font-medium text-text">{title}</span>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <ChevronDown size={16} className="text-text-hint" />
      </motion.div>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 pt-1 space-y-4 border-t border-border">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)
